import { captureMessage, setTag } from '@sentry/react';
import { CONFERENCE_FAILED, CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_UNIQUE_ID_SET, CONFERENCE_WILL_JOIN, CONFERENCE_WILL_LEAVE, DATA_CHANNEL_CLOSED, DATA_CHANNEL_OPENED, KICKED_OUT, P2P_STATUS_CHANGED } from '../conference/actionTypes';
import { CONNECTION_DISCONNECTED, CONNECTION_ESTABLISHED, CONNECTION_FAILED, CONNECTION_WILL_CONNECT } from '../connection/actionTypes';
import { NOTIFY_CAMERA_ERROR, NOTIFY_MIC_ERROR } from '../devices/actionTypes';
import { SET_JWT } from '../jwt/actionTypes';
import { JitsiConferenceEvents, JitsiConnectionErrors } from '../lib-jitsi-meet';
import { SET_AUDIO_MUTED, SET_VIDEO_MUTED } from '../media/actionTypes';
import { PARTICIPANT_JOINED, PARTICIPANT_LEFT } from '../participants/actionTypes';
import MiddlewareRegistry from '../redux/MiddlewareRegistry';
import { TRACK_ADDED, TRACK_REMOVED } from '../tracks/actionTypes';
import { addJourneyStep } from './journey';
import { startMediaHealthCheck, stopMediaHealthCheck } from './media-health';
import { reportConferenceFailed, reportConnectionFailed, reportDeviceError, reportJwtRejected, reportKickedOut } from './report';
import { setSentryUser } from './user';
// прерывание медиа дольше этого — отправляем событие
const LONG_INTERRUPTION_MS = 10000;
let interruptedAt;
/**
 * Подписывается на события конференции, которых нет в redux.
 *
 * @param {Object} conference - JitsiConference.
 * @returns {void}
 */
const subscribeConference = (conference) => {
    conference.on(JitsiConferenceEvents.CONNECTION_INTERRUPTED, () => {
        interruptedAt = Date.now();
        addJourneyStep('media connection interrupted', undefined, 'warning');
    });
    conference.on(JitsiConferenceEvents.CONNECTION_RESTORED, () => {
        const duration = interruptedAt ? Date.now() - interruptedAt : undefined;
        interruptedAt = undefined;
        addJourneyStep('media connection restored', { durationMs: duration });
        if (duration && duration > LONG_INTERRUPTION_MS) {
            captureMessage('Media connection interrupted', {
                level: 'warning',
                fingerprint: ['media-interrupted'],
                tags: {
                    'failure.kind': 'media',
                    'failure.reason': 'interrupted'
                },
                extra: { durationMs: duration }
            });
        }
    });
    conference.on(JitsiConferenceEvents.SUSPEND_DETECTED, () => {
        addJourneyStep('system suspend detected', undefined, 'warning');
    });
};
/**
 * Явно отправляет в sentry сбои, которые видит пользователь, и пишет путь пользователя.
 */
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);
    try {
        switch (action.type) {
            case SET_JWT:
                setSentryUser(store.getState()['features/base/jwt'].user);
                break;
            case CONNECTION_WILL_CONNECT:
                addJourneyStep('xmpp connecting');
                break;
            case CONNECTION_ESTABLISHED:
                addJourneyStep('xmpp connected');
                break;
            case CONNECTION_DISCONNECTED:
                addJourneyStep('xmpp disconnected');
                break;
            case CONNECTION_FAILED: {
                const { jwt } = store.getState()['features/base/jwt'];
                addJourneyStep('xmpp connection failed', { reason: action.error?.name }, 'error');
                reportConnectionFailed(action.error?.name, action.error?.message, action.error?.details);
                // с jwt PASSWORD_REQUIRED означает, что сервер не принял токен
                if (jwt && action.error?.name === JitsiConnectionErrors.PASSWORD_REQUIRED) {
                    reportJwtRejected(jwt, action.error?.name, action.error?.message);
                }
                break;
            }
            case CONFERENCE_WILL_JOIN:
                addJourneyStep('conference joining');
                break;
            case CONFERENCE_JOINED: {
                const { conference } = action;
                setTag('conference.session_id', conference.sessionId);
                setTag('conference.endpoint_id', conference.myUserId());
                addJourneyStep('conference joined', {
                    participants: conference.getParticipantCount(),
                    endpointId: conference.myUserId()
                });
                subscribeConference(conference);
                startMediaHealthCheck(store.getState);
                break;
            }
            case CONFERENCE_UNIQUE_ID_SET:
                setTag('conference.meeting_id', action.conference?.getMeetingUniqueId?.());
                break;
            case CONFERENCE_WILL_LEAVE:
                addJourneyStep('conference leaving');
                stopMediaHealthCheck();
                break;
            case CONFERENCE_LEFT:
                addJourneyStep('conference left');
                stopMediaHealthCheck();
                break;
            case CONFERENCE_FAILED:
                addJourneyStep('conference failed', { reason: action.error?.name }, 'error');
                stopMediaHealthCheck();
                reportConferenceFailed(action.error?.name, action.error?.params);
                break;
            case KICKED_OUT:
                addJourneyStep('kicked out', { by: action.participant?.getId?.() }, 'warning');
                reportKickedOut(action.participant?.getId?.());
                break;
            case P2P_STATUS_CHANGED:
                setTag('conference.p2p', String(action.p2p));
                addJourneyStep(`p2p ${action.p2p ? 'on' : 'off'}`);
                break;
            case DATA_CHANNEL_OPENED:
                addJourneyStep('bridge channel opened');
                break;
            case DATA_CHANNEL_CLOSED:
                addJourneyStep('bridge channel closed', { code: action.code,
                    reason: action.reason }, 'warning');
                break;
            case PARTICIPANT_JOINED:
                !action.participant?.local && addJourneyStep('participant joined', { id: action.participant?.id });
                break;
            case PARTICIPANT_LEFT:
                addJourneyStep('participant left', { id: action.participant?.id });
                break;
            case TRACK_ADDED:
            case TRACK_REMOVED: {
                // в TRACK_REMOVED есть только jitsiTrack
                const jitsiTrack = action.track?.jitsiTrack;
                addJourneyStep(`${jitsiTrack?.isLocal() ? 'local' : 'remote'} track ${action.type === TRACK_ADDED ? 'added' : 'removed'}`, {
                    mediaType: jitsiTrack?.getType(),
                    videoType: jitsiTrack?.videoType,
                    muted: jitsiTrack?.isMuted(),
                    participantId: jitsiTrack?.getParticipantId?.()
                });
                break;
            }
            case SET_AUDIO_MUTED:
                addJourneyStep(`local audio ${action.muted ? 'muted' : 'unmuted'}`);
                break;
            case SET_VIDEO_MUTED:
                addJourneyStep(`local video ${action.muted ? 'muted' : 'unmuted'}`);
                break;
            case NOTIFY_CAMERA_ERROR:
                action.error && reportDeviceError('camera', action.error, store.getState());
                break;
            case NOTIFY_MIC_ERROR:
                action.error && reportDeviceError('microphone', action.error, store.getState());
                break;
        }
    }
    catch (e) {
        // репорт не должен ломать приложение
    }
    return result;
});
